import React, { useState, useMemo } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  FunnelIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  TableCellsIcon,
  CurrencyDollarIcon,
  ReceiptPercentIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { useExpenses } from '../hooks/useExpenses';
import PageHero from '../components/ui/PageHero';
import EmptyState from '../components/ui/EmptyState';
import {
  Expense,
  CreateExpenseDto,
  UpdateExpenseDto,
  ExpenseCategory,
  ExpenseStatus,
  PaymentMethod,
  getExpenseStatusColor,
  getExpenseCategoryDisplay,
  getPaymentMethodDisplay,
  formatCurrency,
} from '../api/expenses';
import { CreateExpenseModal, EditExpenseModal, ViewExpenseModal } from '../components/expenses';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';

export default function Expenses() {
  const {
    expenses,
    analytics,
    statusAnalytics,
    loading,
    error,
    createExpense,
    updateExpense,
    deleteExpense,
    refreshData,
    getTotalSpent,
  } = useExpenses();

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<ExpenseStatus | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<ExpenseCategory | ''>('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<PaymentMethod | ''>('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [sortBy, setSortBy] = useState<keyof Expense>('transactionDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);

  // Filter and sort expenses
  const filteredAndSortedExpenses = useMemo(() => {
    let filtered = expenses.filter(expense => {
      const matchesSearch = expense.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           expense.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (expense.receiptNumber?.toLowerCase().includes(searchTerm.toLowerCase())) ||
                           (expense.vendorId?.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesStatus = !statusFilter || expense.status === statusFilter;
      const matchesCategory = !categoryFilter || expense.category === categoryFilter;
      const matchesPaymentMethod = !paymentMethodFilter || expense.paymentMethod === paymentMethodFilter;
      
      return matchesSearch && matchesStatus && matchesCategory && matchesPaymentMethod;
    });

    // Sort
    filtered.sort((a, b) => {
      let aValue: any = a[sortBy];
      let bValue: any = b[sortBy];
      
      if (sortBy === 'transactionDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [expenses, searchTerm, statusFilter, categoryFilter, paymentMethodFilter, sortBy, sortOrder]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedExpenses.length / itemsPerPage);
  const paginatedExpenses = filteredAndSortedExpenses.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Handle actions
  const handleCreateExpense = async (data: CreateExpenseDto) => {
    const newExpense = await createExpense(data);
    if (newExpense) {
      setShowCreateModal(false);
    }
  };

  const handleUpdateExpense = async (data: UpdateExpenseDto) => {
    if (!selectedExpense) return;
    
    const updatedExpense = await updateExpense(selectedExpense.id, data);
    if (updatedExpense) {
      setShowEditModal(false);
      setSelectedExpense(null);
    }
  };

  const handleDeleteExpense = async () => {
    if (!selectedExpense) return;
    
    const success = await deleteExpense(selectedExpense.id);
    if (success) {
      setShowDeleteModal(false);
      setSelectedExpense(null);
    }
  };

  const handleView = (expense: Expense) => {
    setSelectedExpense(expense);
    setShowViewModal(true);
  };

  const handleEdit = (expense: Expense) => {
    setSelectedExpense(expense);
    setShowEditModal(true);
  };

  const handleDelete = (expense: Expense) => {
    setSelectedExpense(expense);
    setShowDeleteModal(true);
  };

  const clearFilters = () => {
    setStatusFilter('');
    setCategoryFilter('');
    setPaymentMethodFilter('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  if (loading && expenses.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-600"></div>
      </div>
    );
  }

    return (
    <div className="space-y-6">
      {/* Header */}
      <PageHero
        title="Expenses"
        subtitle="Manage and track your business expenses"
        actions={
          <button type="button" onClick={() => setShowCreateModal(true)} className="btn btn-primary focus-pop">
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Add Expense
          </button>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-6 w-6 text-red-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-slate-500 truncate">
                    Total Expenses
                  </dt>
                  <dd className="text-lg font-medium text-slate-900">
                    {formatCurrency(getTotalSpent())}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ReceiptPercentIcon className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-slate-500 truncate">
                    Total Count
                  </dt>
                  <dd className="text-lg font-medium text-slate-900">
                    {expenses.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-6 w-6 bg-yellow-100 rounded-full flex items-center justify-center">
                  <div className="h-3 w-3 bg-yellow-400 rounded-full"></div>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-slate-500 truncate">
                    Pending
                  </dt>
                  <dd className="text-lg font-medium text-slate-900">
                    {expenses.filter(e => e.status === ExpenseStatus.PENDING).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-6 w-6 bg-green-100 rounded-full flex items-center justify-center">
                  <div className="h-3 w-3 bg-green-400 rounded-full"></div>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-slate-500 truncate">
                    Paid
                  </dt>
                  <dd className="text-lg font-medium text-slate-900">
                    {expenses.filter(e => e.status === ExpenseStatus.PAID).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
          <div className="flex items-center w-full rounded-md border border-gray-300 bg-white shadow-sm focus-within:ring-1 focus-within:ring-red-500 focus-within:border-red-500">
              <span className="pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400" aria-hidden="true">
                <MagnifyingGlassIcon className="h-5 w-5" />
              </span>
            <input
              type="text"
                placeholder="Search expenses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 min-w-0 py-2 pr-3 border-0 bg-transparent focus:outline-none focus:ring-0 sm:text-sm"
            />
          </div>
        </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
          </button>

          {/* View Mode Toggle */}
          <div className="flex rounded-md shadow-sm">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-4 py-2 text-sm font-medium rounded-l-md border ${
                viewMode === 'grid'
                  ? 'bg-red-50 border-red-500 text-red-700'
                  : 'bg-white border-gray-300 text-slate-700 hover:bg-slate-50/80'
              }`}
            >
              <Squares2X2Icon className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-4 py-2 text-sm font-medium rounded-r-md border-l-0 border ${
                viewMode === 'table'
                  ? 'bg-red-50 border-red-500 text-red-700'
                  : 'bg-white border-gray-300 text-slate-700 hover:bg-slate-50/80'
              }`}
            >
              <TableCellsIcon className="h-4 w-4" />
            </button>
        </div>
      </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
            <div>
              <label className="block text-sm font-medium text-slate-700">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as ExpenseStatus | '')}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm"
              >
                <option key="empty-option-1753461297372" value="">All Statuses</option>
                {Object.values(ExpenseStatus).map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Category</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value as ExpenseCategory | '')}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm"
              >
                <option key="empty-option-1753461297372" value="">All Categories</option>
                {Object.values(ExpenseCategory).map(category => (
                  <option key={category} value={category}>
                    {getExpenseCategoryDisplay(category)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Payment Method</label>
              <select
                value={paymentMethodFilter}
                onChange={(e) => setPaymentMethodFilter(e.target.value as PaymentMethod | '')}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm"
              >
                <option key="empty-option-1753461297372" value="">All Methods</option>
                {Object.values(PaymentMethod).map(method => (
                  <option key={method} value={method}>
                    {getPaymentMethodDisplay(method)}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
        </div>

      {/* Expenses List */}
      {filteredAndSortedExpenses.length === 0 ? (
        <EmptyState
          title="No expenses found"
          description={
            expenses.length === 0
              ? 'Get started by creating your first expense.'
              : 'Try adjusting your search or filter criteria.'
          }
          icon={<CurrencyDollarIcon className="h-6 w-6" />}
          action={
            expenses.length === 0 ? (
              <button type="button" onClick={() => setShowCreateModal(true)} className="btn btn-primary text-sm focus-pop">
                <PlusIcon className="h-4 w-4" />
                Add Expense
              </button>
            ) : undefined
          }
        />
      ) : (
        <>
      {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginatedExpenses.map((expense) => (
                <div key={expense.id} className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow">
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-slate-900 truncate">
                          {expense.description}
                </h3>
                        <p className="text-sm text-slate-500">
                          {getExpenseCategoryDisplay(expense.category)}
                        </p>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getExpenseStatusColor(expense.status)}`}>
                        {expense.status}
                      </span>
                    </div>
                    
                    <div className="mt-4">
                      <div className="text-2xl font-bold text-red-600">
                        {formatCurrency(expense.amount)}
                    </div>
                      <div className="text-sm text-slate-500">
                        {format(new Date(expense.transactionDate), 'dd MMM yyyy')}
                      </div>
                    </div>

                    <div className="mt-4 flex justify-between items-center">
                      <div className="text-sm text-slate-500">
                        {getPaymentMethodDisplay(expense.paymentMethod)}
              </div>
                      <div className="flex space-x-2">
                    <button
                          onClick={() => handleView(expense)}
                          className="text-slate-400 hover:text-slate-600"
                    >
                          <EyeIcon className="h-4 w-4" />
                    </button>
                    <button
                          onClick={() => handleEdit(expense)}
                          className="text-slate-400 hover:text-cyan-600"
                    >
                          <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                          onClick={() => handleDelete(expense)}
                          className="text-slate-400 hover:text-red-600"
                    >
                          <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-slate-50/80">
                    <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Description
                      </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Category
                      </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Amount
                      </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Status
                      </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Date
                      </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Actions
                      </th>
                    </tr>
                  </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedExpenses.map((expense) => (
                        <tr key={expense.id} className="hover:bg-slate-50/80">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-slate-900">{expense.description}</div>
                        {expense.receiptNumber && (
                          <div className="text-sm text-slate-500">Receipt: {expense.receiptNumber}</div>
                        )}
                          </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                        {getExpenseCategoryDisplay(expense.category)}
                          </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                        {formatCurrency(expense.amount)}
                          </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getExpenseStatusColor(expense.status)}`}>
                          {expense.status}
                        </span>
                          </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                        {format(new Date(expense.transactionDate), 'dd MMM yyyy')}
                          </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                              <button
                            onClick={() => handleView(expense)}
                            className="text-slate-600 hover:text-slate-900"
                              >
                            <EyeIcon className="h-4 w-4" />
                              </button>
                              <button
                            onClick={() => handleEdit(expense)}
                            className="text-cyan-600 hover:text-blue-900"
                              >
                            <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                            onClick={() => handleDelete(expense)}
                                className="text-red-600 hover:text-red-900"
                              >
                            <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                  ))}
                  </tbody>
                </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-700">
                Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                {Math.min(currentPage * itemsPerPage, filteredAndSortedExpenses.length)} of{' '}
                {filteredAndSortedExpenses.length} expenses
          </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
                <span className="px-3 py-2 text-sm font-medium text-slate-700">
                  {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
          </div>
        </div>
      )}
        </>
      )}

      {/* Modals */}
      <CreateExpenseModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateExpense}
      />

      <EditExpenseModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedExpense(null);
        }}
        onUpdate={handleUpdateExpense}
        expense={selectedExpense}
      />

      <ViewExpenseModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedExpense(null);
        }}
        expense={selectedExpense}
        onEdit={() => {
          setShowViewModal(false);
          setShowEditModal(true);
        }}
        onDelete={() => {
          setShowViewModal(false);
          setShowDeleteModal(true);
        }}
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteExpense}
        title="Delete Expense"
        message={`Are you sure you want to delete "${selectedExpense?.description}"? This action cannot be undone.`}
      />
    </div>
  );
} 